import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from './translations/en.js';
import fr from './translations/fr.js';
import es from './translations/es.js';
import de from './translations/de.js';
import zh from './translations/zh.js';
import ja from './translations/ja.js';

// ── Language registry ────────────────────────────────────────────────────────
export const LANGUAGE_MAP = {
    en: { code: 'en', name: 'English',    nativeName: 'English',    flag: '🇬🇧', locale: 'en_US', hreflang: 'en',       dir: 'ltr', cjk: false },
    fr: { code: 'fr', name: 'French',     nativeName: 'Français',   flag: '🇫🇷', locale: 'fr_FR', hreflang: 'fr',       dir: 'ltr', cjk: false },
    es: { code: 'es', name: 'Spanish',    nativeName: 'Español',    flag: '🇪🇸', locale: 'es_ES', hreflang: 'es',       dir: 'ltr', cjk: false },
    de: { code: 'de', name: 'German',     nativeName: 'Deutsch',    flag: '🇩🇪', locale: 'de_DE', hreflang: 'de',       dir: 'ltr', cjk: false },
    zh: { code: 'zh', name: 'Chinese',    nativeName: '中文',        flag: '🇨🇳', locale: 'zh_CN', hreflang: 'zh-Hans', dir: 'ltr', cjk: true  },
    ja: { code: 'ja', name: 'Japanese',   nativeName: '日本語',      flag: '🇯🇵', locale: 'ja_JP', hreflang: 'ja',       dir: 'ltr', cjk: true  },
};

// Flat translation map keyed by language code
const TRANSLATIONS = { en, fr, es, de, zh, ja };

/**
 * Deep-get a nested translation key (e.g. 'shop.addToCart').
 * Falls back to the English string, then to the raw key if both are missing.
 */
function resolveKey(translations, langCode, key) {
    const parts = key.split('.');
    let node = translations[langCode];
    for (const part of parts) {
        if (node == null || typeof node !== 'object') { node = undefined; break; }
        node = node[part];
    }
    // Fallback chain: lang → en → key string
    if (node !== undefined && node !== null && node !== '') return String(node);
    if (langCode !== 'en') return resolveKey(translations, 'en', key);
    return key; // absolute last resort — never returns undefined/blank
}

// ── Module gate helper ────────────────────────────────────────────────────────
/**
 * Returns true if the i18n module is both installed and enabled in the config.
 * Safe to call with null/undefined config.
 */
export function isI18nEnabled(config) {
    return !!(config?.modules?.i18n?.enabled === true);
}

// ── CJK Font loader ───────────────────────────────────────────────────────────
const CJK_FONT_ID = 'omnivaso-cjk-fonts';

function loadCjkFonts() {
    if (typeof document === 'undefined') return;
    if (document.getElementById(CJK_FONT_ID)) return;
    const link = document.createElement('link');
    link.id = CJK_FONT_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&family=Noto+Sans+SC:wght@400;500;700&display=swap';
    document.head.appendChild(link);
}

function unloadCjkFonts() {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(CJK_FONT_ID);
    if (el) el.remove();
}

// ── Context ───────────────────────────────────────────────────────────────────
const LanguageContext = createContext(null);

export const LanguageProvider = ({ children, settings = {}, modules = {} }) => {
    const i18nMod = modules?.i18n || settings?.i18nModule || {};
    const enabled = i18nMod.enabled === true;

    const defaultLanguage = (i18nMod.defaultLanguage || 'en').toLowerCase();
    const enabledLanguages = Array.isArray(i18nMod.enabledLanguages) && i18nMod.enabledLanguages.length > 0
        ? i18nMod.enabledLanguages.map(l => String(l).toLowerCase()).filter(l => LANGUAGE_MAP[l])
        : Object.keys(LANGUAGE_MAP);

    // Guarantee 'en' is always available as fallback even if admin unchecked it
    const safeLanguages = enabledLanguages.includes('en') ? enabledLanguages : ['en', ...enabledLanguages];

    // Active language state — persisted to localStorage
    const [activeLanguage, setActiveLanguageState] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('omnivaso_lang');
            if (saved && safeLanguages.includes(saved.toLowerCase())) {
                return saved.toLowerCase();
            }
        }
        return safeLanguages.includes(defaultLanguage) ? defaultLanguage : 'en';
    });

    // Persist + apply html[lang] + load/unload CJK fonts on language change
    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem('omnivaso_lang', activeLanguage);
        if (typeof document !== 'undefined') {
            document.documentElement.lang = activeLanguage;
            document.documentElement.dir = LANGUAGE_MAP[activeLanguage]?.dir || 'ltr';
        }
        if (LANGUAGE_MAP[activeLanguage]?.cjk) {
            loadCjkFonts();
        } else {
            unloadCjkFonts();
        }
    }, [activeLanguage]);

    // Re-validate active language if module config changes
    useEffect(() => {
        if (!safeLanguages.includes(activeLanguage)) {
            setActiveLanguageState(safeLanguages.includes(defaultLanguage) ? defaultLanguage : 'en');
        }
    }, [safeLanguages.join(','), defaultLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    const setActiveLanguage = useCallback((langCode) => {
        const clean = String(langCode).toLowerCase();
        if (safeLanguages.includes(clean)) {
            setActiveLanguageState(clean);
        }
    }, [safeLanguages.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Translate a dot-notation key.
     * @param {string} key - e.g. 'shop.addToCart', 'nav.home'
     * @param {string} [fallback] - Optional explicit fallback string
     * @returns {string}
     */
    const t = useCallback((key, fallback) => {
        if (!key) return fallback || '';
        const resolved = resolveKey(TRANSLATIONS, activeLanguage, key);
        // If resolveKey returned the raw key (absolute fallback), use provided fallback if any
        if (resolved === key && fallback !== undefined) return fallback;
        return resolved;
    }, [activeLanguage]);

    const languageInfo = LANGUAGE_MAP[activeLanguage] || LANGUAGE_MAP.en;

    return (
        <LanguageContext.Provider value={{
            enabled,
            activeLanguage,
            setActiveLanguage,
            availableLanguages: safeLanguages,
            defaultLanguage,
            languageInfo,
            t,
            LANGUAGE_MAP,
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        // Safe no-op fallback for components rendered outside the provider
        return {
            enabled: false,
            activeLanguage: 'en',
            setActiveLanguage: () => {},
            availableLanguages: ['en'],
            defaultLanguage: 'en',
            languageInfo: LANGUAGE_MAP.en,
            t: (key, fallback) => resolveKey(TRANSLATIONS, 'en', key) || fallback || key,
            LANGUAGE_MAP,
        };
    }
    return ctx;
};
